/**
 * Shared Trend Data Provider Interface
 *
 * This is the shared public TrendDataProvider contract for both web and mobile.
 *
 * The boundary stays JSON-safe so mobile can proxy methods through HTTP without
 * data-shape mismatches. Web implementations can still use `Map` / `Set`
 * internally, but public return values should stay serialisable.
 *
 * @see apps/web/lib/trends/provider-interface.ts for the full web version
 */

import type {
  AllBiomarkersOptions,
  BiomarkerDelta,
  CategoryInsight,
  ChartInsights,
  Correlation,
  CrossBiomarkerInsight,
  Forecast,
  FunctionalSystem,
  GlucoseMetrics,
  InterventionMarker,
  NLQueryResult,
  PersonalizedGoal,
  TrendData,
  TrendQuery,
} from "./types";

// -----------------------------------------------------------------------------
// Available Biomarkers
// -----------------------------------------------------------------------------

/** A biomarker available for a patient (curated or dynamically discovered). */
export interface AvailableBiomarker {
  loincCode: string;
  displayName: string;
  shortName: string;
  unit: string;
  category: FunctionalSystem;
  /** `"curated"` = from BIOMARKER_CONFIG, `"dynamic"` = discovered in observations */
  source: "curated" | "dynamic";
  isLabResult: boolean;
  /** Observation count — populated only for dynamic biomarkers */
  observationCount?: number;
}

/** Result of getAvailableBiomarkers. */
export interface AvailableBiomarkersResult {
  biomarkers: AvailableBiomarker[];
  curated: AvailableBiomarker[];
  dynamic: AvailableBiomarker[];
  /** Effective date range used (may differ from query range after clamping). */
  effectiveDateRange: { start: string; end: string } | null;
  wasDateRangeTruncated: boolean;
}

/** Options for fetching discrete (lab-only) biomarkers. */
export interface DiscreteBiomarkersOptions {
  dateRange?: { start: string; end: string };
  category?: FunctionalSystem;
}

/** Result of getAllBiomarkers / getDiscreteBiomarkers. */
export interface AllBiomarkersResult {
  biomarkers: AvailableBiomarker[];
  configured: AvailableBiomarker[];
  discovered: AvailableBiomarker[];
  identifiersWithData: string[];
  wasObservationCapReached: boolean;
  observationsScanned: number;
}

// -----------------------------------------------------------------------------
// Biomarkers-with-data result
// — uses string[] so it survives JSON serialisation
// -----------------------------------------------------------------------------

/** Result of getBiomarkersWithData. */
export interface BiomarkersWithDataResult {
  /** LOINC codes that have data */
  withData: string[];
  /** Wearable LOINC codes with data (from CQ cache) */
  wearablesWithData: string[];
  /** Lab LOINC codes with data (from FHIR) */
  labsWithData: string[];
  /** All curated LOINC codes that were checked */
  allChecked: string[];
}

// -----------------------------------------------------------------------------
// Provider Interface
// -----------------------------------------------------------------------------

/**
 * Contract for all TrendDataProvider implementations (web and mobile).
 *
 * On web, the concrete implementation reads directly from the Medplum FHIR
 * store (server-side). On mobile, `MobileTrendApiProvider` implements this by
 * calling the web app's `/api/trends` endpoints.
 *
 * The interface is intentionally JSON-safe so mobile can proxy every method
 * through HTTP without data-shape mismatches.
 */
export interface ITrendDataProvider {
  /** Fetch trend data for a single biomarker. */
  getTrendData(query: TrendQuery): Promise<TrendData>;

  /**
   * Batch fetch for the dashboard — returns one entry per LOINC code.
   * Callers should treat the response as a partial map; missing codes
   * indicate no data for that biomarker in the requested window.
   */
  getAllTrendData(
    patientId: string,
    options?: {
      category?: string;
      onlyOutOfRange?: boolean;
      dateRange?: { start: string; end: string };
      /** Optional IANA timezone used for default range selection and local-day bucketing. */
      timezone?: string;
      /** Practice org id — loads per-practice + canonical biomarker label maps when set. */
      practiceId?: string;
    },
  ): Promise<Record<string, TrendData>>;

  /** Fetch CGM-specific aggregate metrics (time-in-range, variability, etc.). */
  getGlucoseMetrics(
    patientId: string,
    dateRange: { start: string; end: string },
    options?: { practiceId?: string },
  ): Promise<GlucoseMetrics>;

  /**
   * Fetch active personalized goal ranges for a set of LOINC codes.
   *
   * Returns `Record<loincCode, PersonalizedGoal>` so callers can treat goals as
   * a JSON-safe dictionary keyed by biomarker code.
   */
  getGoals(
    patientId: string,
    loincCodes: string[],
    options?: { practiceId?: string },
  ): Promise<Record<string, PersonalizedGoal>>;

  /** Fetch intervention markers (e.g. medication changes) to overlay on charts. */
  getInterventions(
    patientId: string,
    dateRange: { start: string; end: string },
  ): Promise<InterventionMarker[]>;

  /**
   * Discover biomarkers with data for a patient.
   * Optional — providers that don't support discovery may omit this.
   */
  getAvailableBiomarkers?(
    patientId: string,
    options?: {
      includeDynamic?: boolean;
      category?: FunctionalSystem;
      dateRange?: { start: string; end: string };
      practiceId?: string;
    },
  ): Promise<AvailableBiomarkersResult>;

  /**
   * All data-backed biomarkers (curated + dynamic).
   * Optional — may be unimplemented on mobile MVP.
   */
  getAllBiomarkers?(
    patientId: string,
    options?: AllBiomarkersOptions,
  ): Promise<AllBiomarkersResult>;

  /** Discrete (lab-only) biomarkers. Optional. */
  getDiscreteBiomarkers?(
    patientId: string,
    options?: DiscreteBiomarkersOptions,
  ): Promise<AllBiomarkersResult>;

  /**
   * Check which curated LOINC codes actually have data without fetching values.
   * Optional — used for efficient overview-card rendering.
   */
  getBiomarkersWithData?(
    patientId: string,
    options?: {
      category?: FunctionalSystem;
      dateRange?: { start: string; end: string };
      practiceId?: string;
    },
  ): Promise<BiomarkersWithDataResult>;
}

/** The history window and horizon for a forecast. */
export interface ForecastWindow {
  dateRange: { start: string; end: string };
  /** Number of days to forecast. Defaults to 7. */
  daysAhead?: number;
}

/**
 * Extended interface for providers that also surface AI/analytics capabilities.
 * All methods are optional — mobile providers can stub them as needed.
 */
export interface ITrendDataProviderWithAnalytics extends ITrendDataProvider {
  getInsights?(
    patientId: string,
    loincCode: string,
    dateRange: { start: string; end: string },
  ): Promise<ChartInsights>;

  getForecast?(patientId: string, loincCode: string, window: ForecastWindow): Promise<Forecast>;

  getCorrelations?(
    patientId: string,
    dateRange: { start: string; end: string },
    loincCodes?: string[],
  ): Promise<Correlation[]>;

  askQuestion?(patientId: string, question: string): Promise<NLQueryResult>;

  getCategoryInsights?(
    patientId: string,
    categories?: FunctionalSystem[],
  ): Promise<CategoryInsight[]>;

  getCrossBiomarkerInsights?(
    patientId: string,
    options?: { category?: FunctionalSystem; dateRange?: { start: string; end: string } },
  ): Promise<CrossBiomarkerInsight[]>;

  getBiomarkerSummaries?(
    patientId: string,
    options?: { category?: FunctionalSystem; dateRange?: { start: string; end: string } },
  ): Promise<Record<string, string>>;

  getChangesSinceEncounter?(
    patientId: string,
    sinceDate: string,
    options?: { category?: FunctionalSystem },
  ): Promise<BiomarkerDelta[]>;
}
